/**
 * RBAC utilities for deriving admin status from a Cognito JWT token.
 */

/**
 * Decodes the JWT payload and returns true if the user is an admin.
 *
 * Rules:
 * - Returns false if token is null/undefined.
 * - Returns true if `cognito:groups` claim is ABSENT (graceful fallback for legacy accounts).
 * - Returns true if `cognito:groups` array includes "retainiq-admins".
 * - Returns false otherwise.
 * - Returns false on any decode/parse error.
 */
export function deriveIsAdmin(token: string | null): boolean {
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    // Graceful fallback: no groups claim → treat as admin (legacy accounts)
    if (!payload['cognito:groups']) return true;
    const groups = payload['cognito:groups'];
    // Ensure groups is an array before checking
    if (!Array.isArray(groups)) return false;
    return groups.includes('retainiq-admins');
  } catch {
    return false;
  }
}

/**
 * Decodes the JWT payload and returns true only if the user is a platform super-admin.
 *
 * Rules (no legacy fallback — super-admin must be explicit):
 * - Returns false if token is null/undefined.
 * - Returns true only if `cognito:groups` array includes "retainiq-superadmins".
 * - Returns false otherwise, including any decode/parse error.
 */
export function deriveIsSuperAdmin(token: string | null): boolean {
  if (!token) return false;
  try {
    const groups = JSON.parse(atob(token.split('.')[1]))['cognito:groups'];
    return Array.isArray(groups) && groups.includes('retainiq-superadmins');
  } catch {
    return false;
  }
}

/**
 * Extracts the `sub` claim (userId) from a JWT token payload.
 * Throws if the token is malformed or the sub claim is missing.
 */
export function extractUserId(token: string): string {
  const payload = JSON.parse(atob(token.split('.')[1]));
  return payload['sub'] as string;
}
